import React, { useState, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Chip,
  FormControl,
  FormHelperText,
  CircularProgress,
  Alert,
  Autocomplete,
  LinearProgress,
  Card,
  CardContent,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import ClothingItemForm from "./ClothingItemForm";
import { ClothingItem, OutfitMetadata } from "../../types";
import { TryOnImageProcessor } from "../../utils/tryOnImageProcessor";

// Common event types for fashion outfits
const EVENT_TYPES = [
  "Casual",
  "Business Casual",
  "Formal",
  "Wedding",
  "Date Night",
  "Party",
  "Graduation",
  "Interview",
  "Vacation",
  "Beach",
  "Workout",
  "Festival",
];

interface OutfitUploadFormProps {
  onSubmit: (file: File, metadata: OutfitMetadata) => Promise<void>;
  isUploading: boolean;
}

interface CompressionStatus {
  isCompressing: boolean;
  progress: number;
  originalSize?: number;
  compressedSize?: number;
  compressionRatio?: number;
  error?: string;
}

const OutfitUploadForm: React.FC<OutfitUploadFormProps> = ({
  onSubmit,
  isUploading,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [clothingItems, setClothingItems] = useState<ClothingItem[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [compressionStatus, setCompressionStatus] = useState<CompressionStatus>({
    isCompressing: false,
    progress: 0,
  });

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const selectedFile = acceptedFiles[0];
      
      // Validate the file first
      const validation = await TryOnImageProcessor.validateForTryOn(selectedFile);
      if (!validation.isValid) {
        setUploadError(validation.error || 'Invalid image file');
        return;
      }

      setFile(selectedFile);
      setUploadError(null);

      // Create preview URL
      const objectUrl = URL.createObjectURL(selectedFile);
      setPreviewUrl(objectUrl);

      // Show compression info if file needs compression
      const processingInfo = TryOnImageProcessor.getProcessingInfo(selectedFile);
      if (processingInfo.willBeCompressed) {
        setCompressionStatus({
          isCompressing: false,
          progress: 0,
          originalSize: selectedFile.size,
        });
      } else {
        setCompressionStatus({
          isCompressing: false,
          progress: 0,
        });
      }

      // Clean up previous preview URL if it exists
      return () => URL.revokeObjectURL(objectUrl);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/jpeg": [],
      "image/png": [],
      "image/webp": [],
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    multiple: false,
  });

  const handleAddClothingItem = (item: ClothingItem) => {
    setClothingItems([...clothingItems, item]);
  };

  const handleRemoveClothingItem = (index: number) => {
    const updatedItems = [...clothingItems];
    updatedItems.splice(index, 1);
    setClothingItems(updatedItems);
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!file) {
      newErrors.file = "Please upload an image";
    }

    if (!eventTypes.length) {
      newErrors.eventTypes = "Please select at least one event type";
    }

    if (!clothingItems.length) {
      newErrors.clothingItems = "Please add at least one clothing item";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    setUploadError(null);

    if (validateForm() && file) {
      const metadata: OutfitMetadata = {
        eventType: eventTypes,
        clothingItems,
        description,
        tags,
      };

      try {
        // Show compression progress if needed
        const processingInfo = TryOnImageProcessor.getProcessingInfo(file);
        if (processingInfo.willBeCompressed) {
          setCompressionStatus(prev => ({
            ...prev,
            isCompressing: true,
            progress: 25,
          }));
        }

        await onSubmit(file, metadata);

        // Reset form on successful upload
        setFile(null);
        setPreviewUrl(null);
        setDescription("");
        setEventTypes([]);
        setTags([]);
        setClothingItems([]);
        setCompressionStatus({
          isCompressing: false,
          progress: 0,
        });
      } catch (error) {
        setUploadError("Failed to upload outfit. Please try again.");
        setCompressionStatus(prev => ({
          ...prev,
          isCompressing: false,
          error: "Upload failed",
        }));
        console.error("Upload error:", error);
      }
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, maxWidth: 800, mx: "auto" }}>
      <Typography variant="h5" gutterBottom>
        Upload Your Outfit
      </Typography>

      {uploadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {uploadError}
        </Alert>
      )}

      {/* Compression Status Display */}
      {file && (compressionStatus.originalSize || compressionStatus.isCompressing) && (
        <Card sx={{ mb: 2, bgcolor: 'background.paper' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Image Processing
            </Typography>
            
            {compressionStatus.originalSize && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Original size: {TryOnImageProcessor.formatFileSize(compressionStatus.originalSize)}
                </Typography>
                {TryOnImageProcessor.getProcessingInfo(file).willBeCompressed && (
                  <Typography variant="body2" color="warning.main">
                    ⚠️ Large image detected - will be compressed for optimal performance
                  </Typography>
                )}
              </Box>
            )}

            {compressionStatus.isCompressing && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" gutterBottom>
                  Compressing image...
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={compressionStatus.progress} 
                  sx={{ mb: 1 }}
                />
              </Box>
            )}

            {compressionStatus.compressedSize && compressionStatus.compressionRatio && (
              <Box>
                <Typography variant="body2" color="success.main">
                  ✅ Compressed to {TryOnImageProcessor.formatFileSize(compressionStatus.compressedSize)}
                  ({(100 - (compressionStatus.compressedSize / (compressionStatus.originalSize || 1) * 100)).toFixed(1)}% reduction)
                </Typography>
              </Box>
            )}

            {compressionStatus.error && (
              <Typography variant="body2" color="error.main">
                ❌ {compressionStatus.error}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      <Box
        {...getRootProps()}
        sx={{
          border: "2px dashed #ccc",
          borderRadius: 2,
          p: 3,
          textAlign: "center",
          cursor: "pointer",
          mb: 2,
          backgroundColor: isDragActive ? "rgba(0, 0, 0, 0.05)" : "transparent",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.05)",
          },
        }}
      >
        <input {...getInputProps()} />
        {previewUrl ? (
          <Box sx={{ position: "relative" }}>
            <img
              src={previewUrl}
              alt="Preview"
              style={{
                maxWidth: "100%",
                maxHeight: "300px",
                objectFit: "contain",
              }}
            />
            <Button
              variant="contained"
              size="small"
              color="primary"
              sx={{ position: "absolute", top: 10, right: 10 }}
              onClick={(e) => {
                e.stopPropagation();
                setFile(null);
                setPreviewUrl(null);
              }}
            >
              Change Image
            </Button>
          </Box>
        ) : (
          <Typography>
            {isDragActive
              ? "Drop the image here..."
              : "Drag and drop an image here, or click to select a file"}
          </Typography>
        )}
      </Box>
      {errors.file && <FormHelperText error>{errors.file}</FormHelperText>}

      <FormControl fullWidth margin="normal">
        <Autocomplete
          multiple
          id="event-types"
          options={EVENT_TYPES}
          value={eventTypes}
          onChange={(_, newValue) => setEventTypes(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Event Types"
              error={!!errors.eventTypes}
              helperText={errors.eventTypes}
              required
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip label={option} {...getTagProps({ index })} key={option} />
            ))
          }
          freeSolo
        />
      </FormControl>

      <TextField
        fullWidth
        margin="normal"
        label="Description"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        multiline
        rows={3}
      />

      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Tags
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <TextField
            label="Add Tag"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAddTag();
              }
            }}
            sx={{ flexGrow: 1, mr: 1 }}
          />
          <Button variant="outlined" onClick={handleAddTag}>
            Add
          </Button>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {tags.map((tag) => (
            <Chip key={tag} label={tag} onDelete={() => handleRemoveTag(tag)} />
          ))}
        </Box>
      </Box>

      <ClothingItemForm
        onAddItem={handleAddClothingItem}
        onRemoveItem={handleRemoveClothingItem}
        items={clothingItems}
      />
      {errors.clothingItems && (
        <FormHelperText error>{errors.clothingItems}</FormHelperText>
      )}

      <Box sx={{ mt: 3, textAlign: "center" }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={handleSubmit}
          disabled={isUploading}
          startIcon={
            isUploading && <CircularProgress size={20} color="inherit" />
          }
        >
          {isUploading ? "Uploading..." : "Upload Outfit"}
        </Button>
      </Box>
    </Paper>
  );
};

export default OutfitUploadForm;
